/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { debounce, DebouncedFunc } from "lodash";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { canRetry, isCancelled, isError, isPending, isSuccess } from "../util/requestStatusUtils";
import { ERequestStatus } from "../util/types";

export interface IUseActionRequestProps<T, TState = unknown> {
  action: () => Promise<T>;
  statusSelector: (state: TState) => ERequestStatus;
  debounceMs?: number;
}

export interface IUseActionRequest {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isCancelled: boolean;
  canRetry: boolean;
  triggerRequest: () => void;
  cancel: () => void;
}

export const useActionRequest = <T = void, TState = unknown>({
  action,
  statusSelector,
  debounceMs = 0,
}: IUseActionRequestProps<T, TState>): IUseActionRequest => {
  const status = useSelector(statusSelector);

  const executeAction: () => Promise<void> = useCallback(async () => {
    await action();
  }, [action]);

  const debouncedAction: (() => Promise<void>) | DebouncedFunc<() => Promise<void>> = useCallback(
    debounceMs > 0 ? debounce(executeAction, debounceMs) : executeAction,
    [executeAction, debounceMs]
  );

  const triggerRequest = useCallback(() => {
    debouncedAction();
  }, [debouncedAction]);

  const cancel = useCallback(() => {
    if (debounceMs > 0 && "cancel" in debouncedAction) {
      (debouncedAction as DebouncedFunc<() => Promise<void>>).cancel();
    }
  }, [debouncedAction, debounceMs]);

  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    isLoading: isPending(status),
    isSuccess: isSuccess(status),
    isError: isError(status),
    isCancelled: isCancelled(status),
    canRetry: canRetry(status),
    triggerRequest,
    cancel,
  };
};
